import { formatDuration } from './formatters'
import {
  type Assessment,
  type AssessmentField,
  type AssessmentFieldResult,
  type CriticalAction,
  type CriticalActionDetailSection,
  type CriticalActionResult,
  type CriticalSubActionEvent,
  type FieldStatus,
  type FieldValue,
  type Flow,
  type PlayerHintEvent,
  type PlayerRun,
  type ResultStatus,
  type RuleResult,
  type RunResult,
  type ScoringResult,
  type ScoringRubric,
  type ScoringRule,
  type Stage,
  type StageResult,
  type SubActionResult,
  type SupplyQuantity,
  type SupplySelectionEvent,
  type SupplySelectionItem,
  type SupplySelectionResult
} from './model'

export function moduleStages(module: { flow?: Flow; rubric?: ScoringRubric; stages?: Stage[] }): Stage[] | undefined {
  return toStages(module.flow, module.rubric) ?? module.stages
}

export function toStages(flow: Flow | undefined, rubric: ScoringRubric | undefined): Stage[] | undefined {
  if (!flow) {
    return undefined
  }
  const criticalActions = rubric?.criticalActions ?? []
  return flow.stages.map(stage => ({
    id: stage.id,
    name: stage.name,
    criticalActions: criticalActions
      .filter(criticalAction => criticalAction.stageId === stage.id)
      .map(criticalAction => ({
        id: criticalAction.id,
        description: criticalAction.displayName,
        rationale: criticalAction.rationale,
        rules: criticalAction.rules,
        children: criticalAction.children,
        domainsOfExcellence: (criticalAction.domainsOfExcellence ?? []).filter(domain => domain !== 'None')
      }))
  }))
}

export function toRunResult(playerRun: PlayerRun, stages: Stage[], assessments: Assessment[] = []): RunResult {
  const criticalActionsById = new Map(playerRun.criticalActions.map(x => [x.id, x]))
  const ruleResultsByCriticalActionId = new Map(
    (playerRun.criticalActionResults ?? []).map(x => [x.id, x.ruleResults ?? []])
  )
  const supplySelectionsByCriticalActionId = new Map(playerRun.supplySelections.map(x => [x.id, x]))
  const criticalSubActionsById = new Map(playerRun.criticalSubActions.map(x => [`${x.parent}:${x.id}`, x]))
  const hintsByCriticalAction = new Map<string, PlayerHintEvent[]>()
  for (const hint of playerRun.hints) {
    if (hint.criticalAction) {
      const hints = hintsByCriticalAction.get(hint.criticalAction) ?? []
      hints.push(hint)
      hintsByCriticalAction.set(hint.criticalAction, hints)
    }
  }

  let totalCriticalActions = 0
  let passedCriticalActions = 0

  const stageResults: StageResult[] = stages.map(stage => {
    let allPassed = true
    let stageHints: PlayerHintEvent[] = []

    const criticalActionResults: CriticalActionResult[] = stage.criticalActions.map(criticalAction => {
      const completed = criticalActionsById.get(criticalAction.id)
      const passed = completed?.passed ?? false
      if (passed) {
        passedCriticalActions++
      } else {
        allPassed = false
      }

      const criticalActionHints = hintsByCriticalAction.get(criticalAction.id) ?? []
      stageHints = [...stageHints, ...criticalActionHints]

      const criticalActionDetails = getCriticalActionDetails(
        criticalAction,
        supplySelectionsByCriticalActionId,
        criticalSubActionsById
      )

      return {
        passed,
        criticalAction,
        hints: criticalActionHints,
        criticalActionDetails,
        ...toAssessmentResult(criticalAction, passed, assessments, ruleResultsByCriticalActionId)
      }
    })

    totalCriticalActions += criticalActionResults.length

    return {
      title: stage.name,
      criticalActionResults,
      hints: stageHints,
      passed: allPassed
    }
  })

  return {
    id: playerRun.id,
    score: playerRun.score,
    baseScore: playerRun.baseScore,
    passed: playerRun.passed,
    isTest: playerRun.isTest,
    completed: playerRun.completed,
    time: playerRun.time,
    hintsUsed: playerRun.hintsUsed,
    failedContaminationCriticalActionCount: playerRun.failedContaminationCriticalActionCount,
    courseId: playerRun.courseId,
    multiplayerSessionId: playerRun.multiplayerSessionId,
    exitModule: playerRun.exitModule,
    stageResults,
    passedCriticalActions,
    totalCriticalActions,
    formattedDuration: formatDuration(playerRun.time),
    techniqueId: playerRun.techniqueId
  }
}

function getCriticalActionDetails(
  criticalAction: CriticalAction,
  supplySelectionsByCriticalActionId: Map<string, SupplySelectionEvent>,
  criticalSubActionsById: Map<string, CriticalSubActionEvent>
): CriticalActionDetailSection[] | undefined {
  const subActions = criticalAction.criticalSubActions ?? []
  const itemsPlaced = supplySelectionsByCriticalActionId.get(criticalAction.id)?.itemsPlaced ?? []
  const completionStatus = (subActionId: string) =>
    toResultStatus(criticalSubActionsById.get(`${criticalAction.id}:${subActionId}`))

  const supplySubActions = subActions
    .map(subAction => ({ subAction, items: subAction.supplySelectionItems ?? [] }))
    .filter(x => x.items.length)

  const requiredSelections: SupplySelectionResult[] = supplySubActions
    .filter(x => x.items.some(item => item.correct))
    .map(({ subAction, items }) => {
      const placed = placedItems(items, itemsPlaced)
      return {
        id: subAction.id,
        status: placed.length ? completionStatus(subAction.id) : 'incomplete',
        expected: toSupplyQuantities(items.filter(item => item.correct)),
        placed: toSupplyQuantities(placed)
      }
    })

  const extraSelections: SupplySelectionResult[] = supplySubActions
    .filter(x => !x.items.some(item => item.correct))
    .flatMap(({ subAction, items }) =>
      placedItems(items, itemsPlaced).map(
        (item): SupplySelectionResult => ({
          id: `${subAction.id}-${item.id}`,
          status: 'extra',
          expected: [],
          placed: [{ name: item.name, count: 1 }]
        })
      )
    )

  const subActionResults: SubActionResult[] = subActions
    .filter(x => !x.supplySelectionItems?.length)
    .map(x => ({ id: x.id, status: completionStatus(x.id), description: x.description }))

  const sections: CriticalActionDetailSection[] = []
  if (requiredSelections.length) {
    sections.push({ kind: 'supplies', items: requiredSelections })
  }
  if (extraSelections.length) {
    sections.push({ kind: 'extraSupplies', items: extraSelections })
  }
  if (subActionResults.length) {
    sections.push({ kind: 'subActions', items: subActionResults })
  }
  return sections.length ? sections : undefined
}

function toAssessmentResult(
  criticalAction: CriticalAction,
  passed: boolean,
  assessments: Assessment[],
  ruleResultsByCriticalActionId: Map<string, RuleResult[]>
): Pick<CriticalActionResult, 'assessment' | 'assessmentField'> {
  const linked = [criticalAction, ...(criticalAction.children ?? [])]
    .map(action => ({ action, assessment: assessments.find(x => x.criticalActionIds.includes(action.id)) }))
    .find(x => x.assessment)
  if (!linked?.assessment) {
    return {}
  }
  const { action: gradingAction, assessment } = linked
  const ruleResultsByRuleId = new Map(
    (ruleResultsByCriticalActionId.get(gradingAction.id) ?? []).map(x => [x.ruleId, x])
  )
  const flattenedRules = (gradingAction.rules ?? []).flatMap(flattenRules)
  const gradingByFieldId = new Map(
    flattenedRules
      .filter(x => fieldIdOf(x))
      .map(x => [fieldIdOf(x)!, { rule: x, ruleResult: ruleResultsByRuleId.get(x.id!) }])
  )
  const rowSavedRule = flattenedRules.find(x => x.activityCompleted)
  const rowSaved = !!rowSavedRule && ruleResultsByRuleId.get(rowSavedRule.id!)?.outcome === 'Passed'
  const gradedFields = assessment.tabs.flatMap(x => x.fields).filter(x => gradingByFieldId.has(x.id) && !x.notGraded)
  const [firstGradedField, ...otherGradedFields] = gradedFields
  if (!firstGradedField) {
    return {}
  }
  if (!otherGradedFields.length) {
    return {
      assessmentField: toAssessmentFieldResult(
        firstGradedField,
        gradingByFieldId.get(firstGradedField.id),
        rowSaved,
        passed
      )
    }
  }
  return {
    assessment: {
      label: assessment.label,
      tabs: assessment.tabs
        .map(tab => ({
          id: tab.id,
          label: tab.label,
          fields: tab.fields
            .filter(x => (gradingByFieldId.has(x.id) || x.prefilled) && !x.notGraded)
            .map(x => toAssessmentFieldResult(x, gradingByFieldId.get(x.id), rowSaved, passed))
        }))
        .filter(x => x.fields.length)
    }
  }
}

export function correctCount(fields: AssessmentFieldResult[]) {
  return fields.filter(x => x.status === 'correct').length
}

type FieldGrading = { rule: ScoringRule; ruleResult?: RuleResult }

function toAssessmentFieldResult(
  field: AssessmentField,
  grading: FieldGrading | undefined,
  rowSaved: boolean,
  passed: boolean
): AssessmentFieldResult {
  const status = passed ? 'correct' : fieldStatus(grading, rowSaved)
  return {
    id: field.id,
    label: field.label,
    status,
    charted: toFieldValue(field, grading?.ruleResult?.actual),
    expected: grading && toFieldValue(field, grading.rule.optionSelected ?? grading.rule.valueEntered)
  }
}

function fieldStatus(grading: FieldGrading | undefined, rowSaved: boolean): FieldStatus {
  if (!rowSaved) {
    return 'notCharted'
  }
  if (!grading) {
    return 'correct'
  }
  const outcome = grading.ruleResult?.outcome
  return outcome ? fieldStatusByOutcome[outcome] : 'notCharted'
}

function toFieldValue(field: AssessmentField, entry: RuleResult['actual']): FieldValue | undefined {
  if (entry?.optionIds) {
    return { optionLabels: optionLabels(field, entry.optionIds) }
  }
  return entry?.value === undefined ? undefined : { value: entry.value, unit: field.unit }
}

function optionLabels(field: AssessmentField, optionIds: string[]): string[] {
  return (field.options ?? []).filter(x => optionIds.includes(x.id)).map(x => x.label)
}

function flattenRules(rule: ScoringRule): ScoringRule[] {
  if (rule.rules) {
    return rule.rules.flatMap(flattenRules)
  }
  if (rule.not) {
    return flattenRules(rule.not)
  }
  return [rule]
}

function fieldIdOf(rule: ScoringRule): string | undefined {
  return rule.optionSelected?.fieldId ?? rule.valueEntered?.fieldId
}

const fieldStatusByOutcome: Record<ScoringResult, FieldStatus> = {
  Passed: 'correct',
  Failed: 'incorrect',
  NotAttempted: 'notCharted'
}

function toResultStatus(completedSubAction: CriticalSubActionEvent | undefined): ResultStatus {
  return completedSubAction ? (completedSubAction.passed ? 'correct' : 'incorrect') : 'incomplete'
}

function placedItems(items: SupplySelectionItem[], itemsPlaced: string[]): SupplySelectionItem[] {
  return itemsPlaced.map(itemId => items.find(item => item.id === itemId)).filter(item => !!item)
}

function toSupplyQuantities(items: SupplySelectionItem[]): SupplyQuantity[] {
  const names = [...new Set(items.map(x => x.name))]
  return names.map(name => ({ name, count: items.filter(x => x.name === name).length }))
}
